import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import InboxIcon from '@mui/icons-material/Inbox';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  useDeleteSubmission,
  useExtractSubmission,
  useReanalyseSubmissionImage,
  useRecordSubmission,
  useUpdateSubmissionImage,
  useUpdateSubmissionWeek,
  useWeightSubmission,
  useWeightSubmissions,
} from '../hooks/useWeightSubmissions';

// ── Design tokens ──────────────────────────────────────────────────────────────

const ACCENT = '#ff9800';
const ACCENT_SOFT = 'rgba(255,152,0,0.12)';
const ACCENT_BORDER = 'rgba(255,152,0,0.2)';
const CARD_BG = 'linear-gradient(135deg, #1a1a1a 0%, #1e1e1e 100%)';

const LABEL_SX = {
  fontSize: '0.68rem',
  textTransform: 'uppercase' as const,
  letterSpacing: '0.6px',
  fontWeight: 600,
  color: `${ACCENT}cc`,
};

const STATUS_STYLE: Record<string, { bg: string; text: string; border: string; label: string }> = {
  pending: {
    bg: 'rgba(255,152,0,0.18)',
    text: '#ffb74d',
    border: 'rgba(255,152,0,0.35)',
    label: 'Pending',
  },
  reviewed: {
    bg: 'rgba(33,150,243,0.18)',
    text: '#90caf9',
    border: 'rgba(33,150,243,0.35)',
    label: 'Reviewed',
  },
  recorded: {
    bg: 'rgba(76,175,80,0.18)',
    text: '#81c784',
    border: 'rgba(76,175,80,0.35)',
    label: 'Recorded',
  },
};

function StatusChip({ status, size = 'small' }: { status: string; size?: 'small' | 'medium' }) {
  const s = STATUS_STYLE[status] ?? {
    bg: 'rgba(255,255,255,0.08)',
    text: 'rgba(255,255,255,0.6)',
    border: 'rgba(255,255,255,0.15)',
    label: status,
  };
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        bgcolor: s.bg,
        color: s.text,
        border: `1px solid ${s.border}`,
        borderRadius: '20px',
        px: size === 'small' ? 1 : 1.5,
        py: size === 'small' ? 0.2 : 0.4,
        fontSize: size === 'small' ? 11 : 12,
        fontWeight: 700,
        letterSpacing: '0.3px',
        lineHeight: 1.4,
        whiteSpace: 'nowrap',
      }}
    >
      {s.label}
    </Box>
  );
}

// ── Time helper ────────────────────────────────────────────────────────────────

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60_000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

// ── Image card ────────────────────────────────────────────────────────────────

function ImageCard({
  image,
  submissionId,
  onLightbox,
}: {
  image: any;
  submissionId: number;
  onLightbox: (url: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [corrected, setCorrected] = useState('');

  const updateImage = useUpdateSubmissionImage(submissionId);
  const reanalyse = useReanalyseSubmissionImage(submissionId);

  const effectiveWeight = image.corrected_weight_g ?? image.extracted_weight_g;
  const isPending = updateImage.isPending || reanalyse.isPending;

  const handleSaveCorrection = () => {
    const g = parseInt(corrected, 10);
    if (!g || g < 10 || g > 5000) return;
    updateImage.mutate({ imageId: image.id, weight: g }, { onSuccess: () => setEditing(false) });
  };

  const handleReanalyse = () => {
    const g = corrected ? parseInt(corrected, 10) : undefined;
    reanalyse.mutate(
      { imageId: image.id, correctedWeightG: g && g >= 10 ? g : undefined },
      { onSuccess: () => setEditing(false) }
    );
  };

  const imgStatusStyle =
    image.status === 'extracted'
      ? { bg: 'rgba(76,175,80,0.85)', text: '#fff' }
      : image.status === 'failed'
        ? { bg: 'rgba(239,83,80,0.85)', text: '#fff' }
        : { bg: 'rgba(0,0,0,0.55)', text: 'rgba(255,255,255,0.75)' };

  return (
    <Box
      sx={{
        borderRadius: 2,
        overflow: 'hidden',
        bgcolor: '#1e1e1e',
        border: `1px solid ${ACCENT_BORDER}`,
        transition: 'border-color 0.15s',
        '&:hover': { borderColor: ACCENT },
      }}
    >
      <Box sx={{ position: 'relative' }}>
        <Box
          component="img"
          src={image.image_url}
          alt="scale"
          onClick={() => onLightbox(image.image_url)}
          sx={{
            width: '100%',
            aspectRatio: '4/3',
            objectFit: 'cover',
            display: 'block',
            cursor: 'zoom-in',
            transition: 'opacity 0.12s',
            '&:hover': { opacity: 0.88 },
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            top: 6,
            right: 6,
            bgcolor: imgStatusStyle.bg,
            color: imgStatusStyle.text,
            fontSize: 10,
            fontWeight: 700,
            px: 0.75,
            py: 0.2,
            borderRadius: 1,
            backdropFilter: 'blur(4px)',
            letterSpacing: '0.3px',
          }}
        >
          {image.status}
        </Box>
        {image.corrected_weight_g && (
          <Box
            sx={{
              position: 'absolute',
              top: 6,
              left: 6,
              bgcolor: 'rgba(33,150,243,0.85)',
              color: '#fff',
              fontSize: 10,
              fontWeight: 700,
              px: 0.75,
              py: 0.2,
              borderRadius: 1,
              backdropFilter: 'blur(4px)',
            }}
          >
            corrected
          </Box>
        )}
      </Box>

      <Box px={1.5} py={1}>
        {editing ? (
          <Stack direction="row" alignItems="center" gap={0.5}>
            <TextField
              size="small"
              type="number"
              value={corrected}
              onChange={(e) => setCorrected(e.target.value)}
              placeholder="g"
              slotProps={{ htmlInput: { min: 10, max: 5000 } }}
              sx={{ flex: 1 }}
              autoFocus
              disabled={isPending}
            />
            <Tooltip title="Re-analyse with AI">
              <span>
                <IconButton
                  size="small"
                  onClick={handleReanalyse}
                  disabled={isPending}
                  sx={{ color: ACCENT }}
                >
                  {reanalyse.isPending ? (
                    <CircularProgress size={14} />
                  ) : (
                    <AutoAwesomeIcon sx={{ fontSize: 16 }} />
                  )}
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Save">
              <span>
                <IconButton
                  size="small"
                  onClick={handleSaveCorrection}
                  disabled={isPending}
                  sx={{ color: '#81c784' }}
                >
                  {updateImage.isPending ? (
                    <CircularProgress size={14} />
                  ) : (
                    <CheckIcon sx={{ fontSize: 16 }} />
                  )}
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Cancel">
              <span>
                <IconButton
                  size="small"
                  onClick={() => setEditing(false)}
                  disabled={isPending}
                  sx={{ opacity: 0.6 }}
                >
                  <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        ) : (
          <Stack direction="row" alignItems="center" justifyContent="space-between" minHeight={38}>
            <Box flex={1} minWidth={0}>
              {effectiveWeight !== null && effectiveWeight !== undefined ? (
                <>
                  <Typography sx={{ ...LABEL_SX, display: 'block', mb: 0.15 }}>
                    {image.corrected_weight_g ? 'Corrected' : 'Extracted'}
                  </Typography>
                  <Typography
                    variant="h6"
                    fontWeight={800}
                    lineHeight={1.1}
                    sx={{ fontVariantNumeric: 'tabular-nums', color: '#f5f5f5' }}
                  >
                    {(effectiveWeight / 1000).toFixed(3)}
                    <Typography
                      component="span"
                      variant="caption"
                      sx={{ color: 'rgba(255,255,255,0.45)', ml: 0.4 }}
                    >
                      kg
                    </Typography>
                  </Typography>
                </>
              ) : image.status === 'failed' ? (
                <Typography variant="caption" sx={{ color: '#ef9a9a' }}>
                  Could not read weight
                </Typography>
              ) : (
                <Typography variant="caption" color="text.disabled">
                  Not yet analysed
                </Typography>
              )}
              {image.claude_note && (
                <Typography
                  variant="caption"
                  sx={{ color: 'rgba(255,255,255,0.35)', display: 'block', mt: 0.25, fontSize: 10 }}
                  noWrap
                >
                  {image.claude_note}
                </Typography>
              )}
            </Box>
            <Tooltip title={effectiveWeight != null ? 'Edit weight' : 'Enter weight'}>
              <IconButton
                size="small"
                onClick={() => {
                  setEditing(true);
                  setCorrected(effectiveWeight != null ? String(effectiveWeight) : '');
                }}
                sx={{ opacity: 0.5, '&:hover': { opacity: 1, color: ACCENT } }}
              >
                <EditIcon sx={{ fontSize: 15 }} />
              </IconButton>
            </Tooltip>
          </Stack>
        )}
      </Box>
    </Box>
  );
}

// ── Review dialog ─────────────────────────────────────────────────────────────

function ReviewDialog({
  submissionId,
  open,
  onClose,
  statusFilter,
}: {
  submissionId: number | null;
  open: boolean;
  onClose: () => void;
  statusFilter: string;
}) {
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  const [group, setGroup] = useState<'main' | 'isolated'>('main');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [extractTriggered, setExtractTriggered] = useState(false);
  const [editingWeek, setEditingWeek] = useState(false);
  const [weekDraft, setWeekDraft] = useState('');

  const { data: submission, isLoading } = useWeightSubmission(submissionId ?? 0, extractTriggered);
  const extract = useExtractSubmission(submissionId ?? 0);
  const record = useRecordSubmission(submissionId ?? 0);
  const del = useDeleteSubmission(statusFilter === 'all' ? null : statusFilter);
  const updateWeek = useUpdateSubmissionWeek(submissionId ?? 0);

  React.useEffect(() => {
    if (!open) setExtractTriggered(false);
  }, [open]);

  React.useEffect(() => {
    const images: any[] = submission?.images ?? [];
    if (extractTriggered && images.length > 0 && !images.some((i: any) => i.status === 'pending')) {
      setExtractTriggered(false);
    }
  }, [submission, extractTriggered]);

  if (!submissionId) return null;

  const images: any[] = submission?.images ?? [];
  const extractedCount = images.filter((i) => i.status === 'extracted').length;
  const failedCount = images.filter((i) => i.status === 'failed').length;
  const pendingCount = images.filter((i) => i.status === 'pending').length;
  const usableCount = images.filter(
    (i) => (i.corrected_weight_g ?? i.extracted_weight_g) !== null
  ).length;
  const isRecorded = submission?.status === 'recorded';

  const DIALOG_SX = {
    '& .MuiDialog-paper': {
      background: '#1a1a1a',
      border: `1px solid ${ACCENT_BORDER}`,
      borderRadius: 2,
    },
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={(_e, reason) => reason !== 'backdropClick' && onClose()}
        maxWidth="md"
        fullWidth
        sx={DIALOG_SX}
      >
        <DialogTitle
          sx={{
            background: `linear-gradient(135deg, ${ACCENT_SOFT}, transparent)`,
            borderBottom: `1px solid ${ACCENT_BORDER}`,
            pb: 1.5,
          }}
        >
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={2}>
            <Box flex={1} minWidth={0}>
              <Stack direction="row" alignItems="center" gap={1.5} flexWrap="wrap" mb={0.5}>
                {/* Week badge / editor */}
                {editingWeek ? (
                  <Stack direction="row" alignItems="center" gap={0.75}>
                    <TextField
                      size="small"
                      type="number"
                      value={weekDraft}
                      onChange={(e) => setWeekDraft(e.target.value)}
                      slotProps={{ htmlInput: { min: 1, max: 20 } }}
                      sx={{ width: 80 }}
                      label="Week"
                      autoFocus
                      disabled={updateWeek.isPending}
                    />
                    <Tooltip title="Save">
                      <span>
                        <IconButton
                          size="small"
                          disabled={updateWeek.isPending || !weekDraft}
                          onClick={() => {
                            const w = parseInt(weekDraft, 10);
                            if (w >= 1 && w <= 19)
                              updateWeek.mutate(w, { onSuccess: () => setEditingWeek(false) });
                          }}
                          sx={{ color: '#81c784' }}
                        >
                          {updateWeek.isPending ? (
                            <CircularProgress size={14} />
                          ) : (
                            <CheckIcon fontSize="small" />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Cancel">
                      <IconButton
                        size="small"
                        onClick={() => setEditingWeek(false)}
                        sx={{ opacity: 0.6 }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                ) : (
                  <Tooltip title="Click to change week">
                    <Box
                      onClick={() => {
                        setWeekDraft(String(submission?.week ?? ''));
                        setEditingWeek(true);
                      }}
                      sx={{
                        background: `linear-gradient(135deg, ${ACCENT}, #ffa726)`,
                        color: '#000',
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.5,
                        borderRadius: 1.5,
                        px: 1.25,
                        py: 0.4,
                        fontSize: 13,
                        fontWeight: 800,
                        lineHeight: 1,
                        transition: 'opacity 0.12s',
                        '&:hover': { opacity: 0.85 },
                      }}
                    >
                      Wk {submission?.week}
                      <EditIcon sx={{ fontSize: 11 }} />
                    </Box>
                  </Tooltip>
                )}
                <Typography
                  variant="h6"
                  fontWeight={800}
                  sx={{ color: '#f5f5f5', lineHeight: 1.2 }}
                >
                  {submission?.block_name}
                </Typography>
              </Stack>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)' }}>
                {submission?.batch_name} · {submission?.submitted_by?.name}
                {submission?.created_at ? ` · ${timeAgo(submission.created_at)}` : ''}
              </Typography>
            </Box>
            {submission && <StatusChip status={submission.status} />}
          </Stack>
        </DialogTitle>

        <DialogContent dividers sx={{ borderColor: ACCENT_BORDER, bgcolor: '#161616' }}>
          {isLoading ? (
            <Box display="flex" justifyContent="center" py={6}>
              <CircularProgress sx={{ color: ACCENT }} />
            </Box>
          ) : (
            <Stack spacing={2}>
              {/* Action bar */}
              {!isRecorded && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    flexWrap: 'wrap',
                    p: 1.5,
                    bgcolor: ACCENT_SOFT,
                    border: `1px solid ${ACCENT_BORDER}`,
                    borderRadius: 1.5,
                  }}
                >
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={
                      extract.isPending || extractTriggered ? (
                        <CircularProgress size={14} sx={{ color: '#000' }} />
                      ) : (
                        <AutoAwesomeIcon />
                      )
                    }
                    onClick={() =>
                      extract.mutate(undefined, { onSuccess: () => setExtractTriggered(true) })
                    }
                    disabled={extract.isPending || extractTriggered || pendingCount === 0}
                    sx={{
                      background: `linear-gradient(135deg, ${ACCENT}, #ffa726)`,
                      color: '#000',
                      fontWeight: 700,
                      '&:hover': { background: `linear-gradient(135deg, #f57c00, ${ACCENT})` },
                      '&:disabled': { opacity: 0.45, background: ACCENT },
                    }}
                  >
                    {extract.isPending || extractTriggered
                      ? 'Analysing…'
                      : `Analyse ${pendingCount} photo${pendingCount !== 1 ? 's' : ''}`}
                  </Button>
                  {(extractedCount > 0 || failedCount > 0) && (
                    <Box display="flex" gap={1} flexWrap="wrap">
                      {extractedCount > 0 && (
                        <Chip
                          label={`${extractedCount} extracted`}
                          size="small"
                          sx={{
                            bgcolor: 'rgba(76,175,80,0.2)',
                            color: '#81c784',
                            border: '1px solid rgba(76,175,80,0.3)',
                            fontWeight: 600,
                            fontSize: 11,
                          }}
                        />
                      )}
                      {failedCount > 0 && (
                        <Chip
                          label={`${failedCount} failed`}
                          size="small"
                          sx={{
                            bgcolor: 'rgba(239,83,80,0.2)',
                            color: '#ef9a9a',
                            border: '1px solid rgba(239,83,80,0.3)',
                            fontWeight: 600,
                            fontSize: 11,
                          }}
                        />
                      )}
                      {pendingCount > 0 && (
                        <Chip
                          label={`${pendingCount} pending`}
                          size="small"
                          sx={{
                            bgcolor: 'rgba(255,152,0,0.2)',
                            color: '#ffb74d',
                            border: '1px solid rgba(255,152,0,0.3)',
                            fontWeight: 600,
                            fontSize: 11,
                          }}
                        />
                      )}
                    </Box>
                  )}
                </Box>
              )}

              {isRecorded && (
                <Alert
                  severity="success"
                  icon={<CheckCircleIcon />}
                  sx={{
                    bgcolor: 'rgba(76,175,80,0.12)',
                    border: '1px solid rgba(76,175,80,0.3)',
                    color: '#a5d6a7',
                    '& .MuiAlert-icon': { color: '#81c784' },
                  }}
                >
                  These weights have been recorded into the flock&apos;s weekly data.
                </Alert>
              )}

              {/* Image grid */}
              <Box display="flex" flexWrap="wrap" gap={1.5}>
                {images.map((img) => (
                  <Box
                    key={img.id}
                    sx={{ width: { xs: 'calc(50% - 6px)', sm: 'calc(33.33% - 8px)' } }}
                  >
                    <ImageCard
                      image={img}
                      submissionId={submissionId}
                      onLightbox={setLightboxUrl}
                    />
                  </Box>
                ))}
              </Box>
            </Stack>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            borderTop: `1px solid ${ACCENT_BORDER}`,
            px: 2.5,
            py: 1.5,
            gap: 1,
            background: '#1a1a1a',
          }}
        >
          <Button onClick={onClose} sx={{ color: 'text.secondary' }}>
            Close
          </Button>
          <Box flex={1} />
          {!isRecorded && (
            <Tooltip title="Delete submission">
              <IconButton
                color="error"
                onClick={() => setConfirmDelete(true)}
                disabled={del.isPending}
                sx={{ opacity: 0.65, '&:hover': { opacity: 1 } }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {!isRecorded && (
            <>
              <FormControl size="small" sx={{ minWidth: 130 }}>
                <InputLabel>Group</InputLabel>
                <Select
                  value={group}
                  label="Group"
                  onChange={(e) => setGroup(e.target.value as 'main' | 'isolated')}
                >
                  <MenuItem value="main">Main</MenuItem>
                  <MenuItem value="isolated">Isolated</MenuItem>
                </Select>
              </FormControl>
              <Button
                variant="contained"
                startIcon={
                  record.isPending ? (
                    <CircularProgress size={14} sx={{ color: '#fff' }} />
                  ) : (
                    <CheckCircleIcon />
                  )
                }
                onClick={() => record.mutate(group, { onSuccess: onClose })}
                disabled={record.isPending || usableCount === 0}
                sx={{
                  background: 'linear-gradient(135deg, #2e7d32, #388e3c)',
                  color: '#fff',
                  fontWeight: 700,
                  '&:hover': { background: 'linear-gradient(135deg, #388e3c, #43a047)' },
                  '&:disabled': { opacity: 0.4 },
                }}
              >
                {record.isPending
                  ? 'Recording…'
                  : `Record ${usableCount} weight${usableCount !== 1 ? 's' : ''}`}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        maxWidth="xs"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            background: '#1a1a1a',
            border: '1px solid rgba(239,83,80,0.3)',
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle sx={{ color: '#ef9a9a', fontWeight: 800 }}>Delete submission?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            This will permanently delete the submission and all uploaded photos. This cannot be
            undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ borderTop: '1px solid rgba(239,83,80,0.15)', px: 2, py: 1.5 }}>
          <Button onClick={() => setConfirmDelete(false)} sx={{ color: 'text.secondary' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            disabled={del.isPending}
            startIcon={
              del.isPending ? <CircularProgress size={14} color="inherit" /> : <DeleteIcon />
            }
            onClick={() => {
              if (!submissionId) return;
              del.mutate(submissionId, {
                onSuccess: () => {
                  setConfirmDelete(false);
                  onClose();
                },
              });
            }}
          >
            {del.isPending ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Lightbox */}
      <Dialog
        open={!!lightboxUrl}
        onClose={() => setLightboxUrl(null)}
        maxWidth="lg"
        sx={{ '& .MuiDialog-paper': { bgcolor: '#000', boxShadow: 'none' } }}
      >
        <DialogContent sx={{ p: 0 }}>
          <Box
            component="img"
            src={lightboxUrl ?? ''}
            alt="scale"
            sx={{ maxHeight: '90vh', maxWidth: '90vw', display: 'block' }}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}

// ── Submission row ────────────────────────────────────────────────────────────

function SubmissionRow({
  sub,
  onOpen,
  onDelete,
  deleting,
}: {
  sub: any;
  onOpen: () => void;
  onDelete: () => void;
  deleting: boolean;
}) {
  const isRecorded = sub.status === 'recorded';

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'stretch',
        background: CARD_BG,
        border: `1px solid ${ACCENT_BORDER}`,
        borderRadius: 2,
        overflow: 'hidden',
        opacity: isRecorded ? 0.55 : 1,
        transition: 'border-color 0.15s, box-shadow 0.15s, opacity 0.15s',
        '&:hover': {
          borderColor: isRecorded ? ACCENT_BORDER : ACCENT,
          boxShadow: isRecorded ? 'none' : `0 0 0 1px ${ACCENT}22, 0 4px 20px rgba(0,0,0,0.4)`,
        },
      }}
    >
      {/* Week badge */}
      <Box
        sx={{
          minWidth: 58,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          background: `linear-gradient(180deg, ${ACCENT} 0%, #ffa726 100%)`,
          color: '#000',
          flexShrink: 0,
        }}
      >
        <Typography
          sx={{
            fontSize: 9,
            fontWeight: 800,
            letterSpacing: '0.8px',
            opacity: 0.65,
            textTransform: 'uppercase',
          }}
        >
          WK
        </Typography>
        <Typography
          sx={{ fontSize: 24, fontWeight: 800, lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}
        >
          {sub.week}
        </Typography>
      </Box>

      {/* Main content */}
      <Box sx={{ flex: 1, px: 2, py: 1.5, cursor: 'pointer', minWidth: 0 }} onClick={onOpen}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={1}>
          <Box minWidth={0} flex={1}>
            <Typography fontWeight={700} noWrap sx={{ fontSize: 15, color: '#f5f5f5' }}>
              {sub.block_name}
            </Typography>
            <Typography variant="body2" color="text.secondary" noWrap sx={{ fontSize: 13 }}>
              {sub.batch_name}
            </Typography>
            <Box display="flex" alignItems="center" gap={0.75} mt={0.5} flexWrap="wrap">
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.35)', fontSize: 11 }}>
                {sub.image_count} photo{sub.image_count !== 1 ? 's' : ''}
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.2)', fontSize: 11 }}>
                ·
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.35)', fontSize: 11 }}>
                {sub.submitted_by?.name}
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.2)', fontSize: 11 }}>
                ·
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.35)', fontSize: 11 }}>
                {timeAgo(sub.created_at)}
              </Typography>
            </Box>
          </Box>
          <StatusChip status={sub.status} />
        </Stack>
      </Box>

      {/* Quick delete */}
      {!isRecorded && (
        <Box sx={{ display: 'flex', alignItems: 'center', px: 1.25 }}>
          <Tooltip title="Delete">
            <IconButton
              size="small"
              color="error"
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
              }}
              disabled={deleting}
              sx={{ opacity: 0.45, '&:hover': { opacity: 1 } }}
            >
              {deleting ? <CircularProgress size={15} /> : <DeleteIcon sx={{ fontSize: 17 }} />}
            </IconButton>
          </Tooltip>
        </Box>
      )}
    </Box>
  );
}

// ── Segmented tab ─────────────────────────────────────────────────────────────

function TabBar({
  value,
  onChange,
  counts,
}: {
  value: string;
  onChange: (v: string) => void;
  counts: Record<string, number>;
}) {
  const tabs = [
    { key: 'pending', label: 'Pending' },
    { key: 'reviewed', label: 'Reviewed' },
    { key: 'recorded', label: 'Recorded' },
    { key: 'all', label: 'All' },
  ];

  return (
    <Box
      sx={{
        display: 'flex',
        bgcolor: '#1a1a1a',
        border: `1px solid ${ACCENT_BORDER}`,
        borderRadius: 2,
        p: 0.5,
        gap: 0.5,
        mb: 2.5,
      }}
    >
      {tabs.map((t) => {
        const active = value === t.key;
        const count = counts[t.key] ?? 0;
        return (
          <Box
            key={t.key}
            onClick={() => onChange(t.key)}
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.75,
              py: 0.75,
              px: 1,
              borderRadius: 1.5,
              cursor: 'pointer',
              background: active ? `linear-gradient(135deg, ${ACCENT}, #ffa726)` : 'transparent',
              color: active ? '#000' : 'rgba(255,255,255,0.5)',
              fontWeight: active ? 700 : 500,
              fontSize: 13,
              transition: 'background 0.15s, color 0.15s',
              '&:hover': !active
                ? { bgcolor: 'rgba(255,152,0,0.08)', color: 'rgba(255,255,255,0.8)' }
                : {},
              userSelect: 'none',
            }}
          >
            {t.label}
            {count > 0 && (
              <Box
                sx={{
                  bgcolor: active ? 'rgba(0,0,0,0.25)' : ACCENT_SOFT,
                  color: active ? '#000' : ACCENT,
                  border: active ? 'none' : `1px solid ${ACCENT_BORDER}`,
                  borderRadius: '10px',
                  px: 0.6,
                  fontSize: 10,
                  fontWeight: 800,
                  lineHeight: 1.6,
                  minWidth: 18,
                  textAlign: 'center',
                }}
              >
                {count}
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function WeightSubmissionsInbox() {
  const [tab, setTab] = useState<string>('pending');
  const [openId, setOpenId] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const {
    data: submissions,
    isLoading,
    refetch,
  } = useWeightSubmissions(tab === 'all' ? undefined : tab);
  const { data: allSubmissions } = useWeightSubmissions(undefined);
  const del = useDeleteSubmission(tab === 'all' ? null : tab);

  const counts = {
    pending: allSubmissions?.filter((s: any) => s.status === 'pending').length ?? 0,
    reviewed: allSubmissions?.filter((s: any) => s.status === 'reviewed').length ?? 0,
    recorded: allSubmissions?.filter((s: any) => s.status === 'recorded').length ?? 0,
  };

  const handleDelete = (id: number) => {
    setDeletingId(id);
    del.mutate(id, { onSettled: () => setDeletingId(null) });
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 860, mx: 'auto' }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 1,
          mb: 3,
          pb: 2.5,
          borderBottom: `1px solid ${ACCENT_BORDER}`,
        }}
      >
        <Box>
          <Typography variant="h5" fontWeight={800}>
            Weight Submissions
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Scale photos submitted by supervisors for review
          </Typography>
        </Box>
        <Tooltip title="Refresh">
          <IconButton
            onClick={() => refetch()}
            size="small"
            sx={{
              border: `1px solid ${ACCENT_BORDER}`,
              '&:hover': { bgcolor: ACCENT_SOFT, borderColor: ACCENT },
            }}
          >
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Tab bar */}
      <TabBar value={tab} onChange={setTab} counts={counts} />

      {/* List */}
      {isLoading ? (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          py={8}
          gap={2}
        >
          <CircularProgress sx={{ color: ACCENT }} />
          <Typography variant="body2" color="text.secondary">
            Loading submissions…
          </Typography>
        </Box>
      ) : !submissions || submissions.length === 0 ? (
        <Box
          sx={{
            textAlign: 'center',
            py: 8,
            px: 3,
            background: CARD_BG,
            border: `1px dashed ${ACCENT_BORDER}`,
            borderRadius: 3,
          }}
        >
          <InboxIcon
            sx={{ fontSize: 52, color: ACCENT_BORDER, mb: 2, display: 'block', mx: 'auto' }}
          />
          <Typography fontWeight={700} mb={0.5}>
            No {tab !== 'all' ? tab : ''} submissions
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {tab === 'pending'
              ? 'Submissions from the worker app will appear here.'
              : tab === 'reviewed'
                ? 'Submissions awaiting AI analysis will appear here.'
                : 'Nothing to show for this filter.'}
          </Typography>
        </Box>
      ) : (
        <Stack spacing={1}>
          {submissions.map((sub: any) => (
            <SubmissionRow
              key={sub.id}
              sub={sub}
              onOpen={() => setOpenId(sub.id)}
              onDelete={() => handleDelete(sub.id)}
              deleting={deletingId === sub.id}
            />
          ))}
        </Stack>
      )}

      <ReviewDialog
        submissionId={openId}
        open={openId !== null}
        onClose={() => setOpenId(null)}
        statusFilter={tab}
      />
    </Box>
  );
}
